import React, { useState } from "react";
import { withFormik } from "formik";
import { css } from '@emotion/css';
import styled from '@emotion/styled';
import * as yup from 'yup';
import moment from 'moment';
import ApplicationInformationForm, { ApplicationInformationFormData, ApplicationInformationFormProps } from '../../formScenes/ApplicationInformationForm';
import UpdateApplicationRequest from "../../../models/applications/api/UpdateApplicationRequest";
import UpdateApplicationInformationByLanguageRequest from "../../../models/applications/api/UpdateApplicationInformationByLanguageRequest";
import UpdateApplicationEnvironmentRequest from "../../../models/applications/api/environments/UpdateApplicationEnvironmentRequest";
import * as applicationsApi from '../../../api/controllers/applications';
import SmallModalLayout from '../../../components/structure/SmallModalLayout';
import ModalHeader from '../../../components/general/ModalHeader';
import Button from '../../../components/general/Button';

interface UpdateApplicationFormInnerProps extends ApplicationInformationFormProps {
    onSubmitForm: (values: ApplicationInformationFormData, setStatus: (status?: any) => void) => Promise<void>;
}

const buildUpdateRequests = (props: ApplicationInformationFormProps, values: ApplicationInformationFormData) => {
    var featureFlags = props.appContainer.state.featureFlags;
    const newFlags = featureFlags.filter(f => values.selectedFlagIds.includes(f.id));
    const sharedRequestFields = {
        timeZone: values.timeZone,
        featureFlags: newFlags,
        manageMenuApplicationId: values.manageMenuApplicationId,
        posName: values.posName,
        posLocationId: values.posLocationId,
    };

    const sharedContentFields = {
        name: values.name,
        invocationPhrase: values.invocationPhrase,
        shortDescription: values.shortDescription,
        description: values.description,
        keywords: values.keywords,
        ...sharedRequestFields,
    };

    let updateApplicationRequest: UpdateApplicationRequest = {
        imageItemId: values.imageItemId,
        name: props.app.name,
        invocationPhrase: props.app.invocationPhrase,
        shortDescription: props.app.shortDescription,
        description: props.app.description,
        keywords: props.app.keywords,
        ...sharedRequestFields,
        applicationInformationItems: props.app.applicationInformationItems ? props.app.applicationInformationItems : [],
        OutboundCallIntegrationName: values.outboundCallIntegrationName,
        OutboundCallLocationId: values.outboundCallLocationId,
        isLive: values.isLive,
    };

    let updateEnvironmentRequest: UpdateApplicationEnvironmentRequest = {
        imageItemId: values.imageItemId,
        name: props.environment?.name,
        applicationName: props.app.name,
        invocationPhrase: props.app.invocationPhrase,
        shortDescription: props.app.shortDescription,
        description: props.app.description,
        keywords: props.app.keywords,
        ...sharedRequestFields,
        applicationInformationItems: props.app.applicationInformationItems ? props.app.applicationInformationItems : [],
    };

    const upsertLanguageItem = (
        items: UpdateApplicationInformationByLanguageRequest[],
        languageId: string,
        environmentId?: string,
    ) => {
        const itemFields = {
            languageId,
            environmentId,
            ...sharedContentFields,
        };

        return items.some(a => a.languageId === languageId)
            ? items.map(item => item.languageId === languageId ? { ...item, ...itemFields } : item)
            : [...items, itemFields];
    };

    if (!props.languageIds || props.languageIds.length < 1) {
        updateApplicationRequest = {
            ...updateApplicationRequest,
            ...sharedContentFields,
        };
        updateEnvironmentRequest = {
            ...updateEnvironmentRequest,
            ...sharedContentFields,
            applicationName: values.name,
        };
    } else {
        props.languageIds.forEach(languageId => {
            updateApplicationRequest = {
                ...updateApplicationRequest,
                applicationInformationItems: upsertLanguageItem(
                    updateApplicationRequest.applicationInformationItems,
                    languageId,
                    props.environment.id,
                )
            };

            updateEnvironmentRequest = {
                ...updateEnvironmentRequest,
                applicationInformationItems: upsertLanguageItem(
                    updateEnvironmentRequest.applicationInformationItems,
                    languageId,
                    props.environment?.id,
                )
            };
        });
    }

    return { updateApplicationRequest, updateEnvironmentRequest };
};

const submitApplicationUpdate = async (
    props: ApplicationInformationFormProps,
    values: ApplicationInformationFormData,
    setStatus: (status?: any) => void,
    launchDate?: string,
) => {
    if (props.languageIds?.length && !props.environment?.id) {
        setStatus({ message: 'Environment is still loading. Please try again.' });
        return;
    }

    const { updateApplicationRequest, updateEnvironmentRequest } = buildUpdateRequests(props, values);
    if (launchDate) {
        updateApplicationRequest.launchDate = launchDate;
    }

    await props.appContainer.updateApplication(props.app.id, updateApplicationRequest).then(async result => {
        if (values.imageFile) {
            await props.appContainer.updateApplicationImage(props.app.id, values.imageFile).then(imgResult => {
                updateEnvironmentRequest.imageItemId = imgResult?.data?.id;
            });
        }

        if (values.posConfig && values.posConfig.length > 0) {
            await applicationsApi.updatePOSConfig(props.app.id, values.posConfig);
        }

        let message = "";
        if (result.resultType === "Ok") {
            message = "Application updated successfully";
        } else {
            message = "There was an error updating the application";
        }
        setTimeout(() => {
            setStatus({ message });
        }, 5000);
    });

    if (props.environmentOption === "live" && props.environment) {
        props.environmentsContainer.updateEnvironmentSettings(props.app.id, props.environment.id, updateEnvironmentRequest);
    }
};

const UpdateApplicationFormInner: React.FC<UpdateApplicationFormInnerProps> = (props) => {
    const [isLaunchDateModalVisible, setIsLaunchDateModalVisible] = useState(false);
    const [pendingValues, setPendingValues] = useState<ApplicationInformationFormData | null>(null);
    const [pendingSetStatus, setPendingSetStatus] = useState<((status?: any) => void) | null>(null);

    const currentLaunchDate = props.app.launchDate;
    const formattedCurrentLaunchDate = currentLaunchDate ? moment.utc(currentLaunchDate).format('MM/DD/YYYY') : '';
    const formattedToday = moment().format('MM/DD/YYYY');
    const requestLaunchDate = moment().format('YYYY-MM-DD');

    const closeLaunchDateModal = () => {
        setIsLaunchDateModalVisible(false);
        setPendingValues(null);
        setPendingSetStatus(null);
    };

    const handleLaunchDateDecision = async (updateLaunchDate: boolean) => {
        if (!pendingValues || !pendingSetStatus) {
            closeLaunchDateModal();
            return;
        }

        await submitApplicationUpdate(
            props,
            pendingValues,
            pendingSetStatus,
            updateLaunchDate ? requestLaunchDate : undefined,
        );

        closeLaunchDateModal();
    };

    const handleSubmitForm = async (values: ApplicationInformationFormData, setStatus: (status?: any) => void) => {
        const isGoingLive = props.app.isLive === false && values.isLive === true;
        const hasExistingLaunchDate = !!props.app.launchDate;

        if (!isGoingLive) {
            await submitApplicationUpdate(props, values, setStatus);
            return;
        }

        if (!hasExistingLaunchDate) {
            await submitApplicationUpdate(props, values, setStatus, requestLaunchDate);
            return;
        }

        setPendingValues(values);
        setPendingSetStatus(() => setStatus);
        setIsLaunchDateModalVisible(true);
    };

    return (
        <>
            <FormikUpdateApplicationForm {...props} onSubmitForm={handleSubmitForm} />
            <SmallModalLayout className={launchDateModalStyle} isVisible={isLaunchDateModalVisible} onClose={closeLaunchDateModal}>
                <ModalHeader
                    title="Update Launch Date?"
                    onClose={closeLaunchDateModal}
                />
                <LaunchDateModalBody>
                    <LaunchDateModalText>
                        {`Would you like to update the launch date from ${formattedCurrentLaunchDate} to ${formattedToday}? (This cannot be undone)`}
                    </LaunchDateModalText>
                </LaunchDateModalBody>
                <LaunchDateModalFooter>
                    <Button
                        type="button"
                        themes={["primary"]}
                        text="Yes, Update"
                        onClick={() => handleLaunchDateDecision(true)}
                    />
                    <Button
                        type="button"
                        themes={["secondary"]}
                        text="No, Keep Current"
                        onClick={() => handleLaunchDateDecision(false)}
                    />
                </LaunchDateModalFooter>
            </SmallModalLayout>
        </>
    );
};

const FormikUpdateApplicationForm = withFormik<UpdateApplicationFormInnerProps, ApplicationInformationFormData>({
    mapPropsToValues: props => {

        const firstAppInfo = props.app?.applicationInformationItems?.find(a => a.languageId === props.languageIds[0]);
        if (!props.languageIds || props.languageIds.length < 1 || !firstAppInfo) {
            const formValuesFromProps: ApplicationInformationFormData = {
                name: props.app.name ? props.app.name : '',
                invocationPhrase: props.app.invocationPhrase ? props.app.invocationPhrase : '',
                shortDescription: props.app.shortDescription ? props.app.shortDescription : '',
                description: props.app.description ? props.app.description : '',
                keywords: props.app.keywords ? props.app.keywords : '',
                timeZone: props.app.timeZone ? props.app.timeZone : '',
                posConfig: null,
                imageItemId: props.app.imageItemId,
                imageFile: null,
                selectedFlagIds: props.app.featureFlags ? props.app.featureFlags?.map(f => f.id) : [],
                featureFlags: props.app.featureFlags ? props.app.featureFlags : [],
                posLocationId: props.app.posLocationId ? props.app.posLocationId : '',
                posName: props.app.posName ? props.app.posName : '',
                manageMenuApplicationId: props.app.manageMenuApplicationId ? props.app.manageMenuApplicationId : '',
                outboundCallIntegrationName: props.app.outboundCallIntegrationName ? props.app.outboundCallIntegrationName : '',
                outboundCallLocationId: props.app.outboundCallLocationId ? props.app.outboundCallLocationId : '',
                isLive: props.app.isLive ?? false,
            };
            return formValuesFromProps;
        }
        else {
            const formValuesFromFirstApp: ApplicationInformationFormData = {
                name: firstAppInfo.name ? firstAppInfo.name : '',
                invocationPhrase: firstAppInfo.invocationPhrase ? firstAppInfo.invocationPhrase : '',
                shortDescription: firstAppInfo.shortDescription ? firstAppInfo.shortDescription : '',
                description: firstAppInfo.description ? firstAppInfo.description : '',
                keywords: firstAppInfo.keywords ? firstAppInfo.keywords : '',
                timeZone: firstAppInfo.timeZone ? firstAppInfo.timeZone : '',
                posConfig: null,
                imageItemId: props.app.imageItemId,
                imageFile: null,
                selectedFlagIds: firstAppInfo.featureFlags ? firstAppInfo.featureFlags?.map(f => f.id) : [],
                featureFlags: firstAppInfo.featureFlags ? firstAppInfo.featureFlags : [],
                posLocationId: props.app.posLocationId ? props.app.posLocationId : '',
                posName: props.app.posName ? props.app.posName : '',
                manageMenuApplicationId: props.app.manageMenuApplicationId ? props.app.manageMenuApplicationId : '',
                outboundCallIntegrationName: props.app.outboundCallIntegrationName ? props.app.outboundCallIntegrationName : '',
                outboundCallLocationId: props.app.outboundCallLocationId ? props.app.outboundCallLocationId : '',
                isLive: props.app.isLive ?? false,
            };
            return formValuesFromFirstApp;
        }
    },
    validationSchema: yup.object().shape({
        name: yup.string().max(50).required("You must enter a name for your application"),
        shortDescription: yup.string().max(160),
        description: yup.string().max(4000),
        invocationPhrase: yup.string().required("You must enter an invocation name for your application").matches(/^([^0-9\!\&\*\(\)\{\}\%\$\#\@\!\?\;\:\-\_\+\=\/\<\>\~]*)$/, "Invocation name cannot contain digits or special characters"),
        outboundCallLocationId: yup.string().nullable(),
    }),
    handleSubmit: async (values, { props, setStatus }) => {
        await props.onSubmitForm(values, setStatus);
    }
})(ApplicationInformationForm);

const launchDateModalStyle = css`
    max-width: 640px;
    min-height: 0;
    height: auto;
`;

const LaunchDateModalBody = styled.div`
    padding: 32px 32px 16px;
    text-align: center;
`;

const LaunchDateModalText = styled.p`
    font-size: 18px;
    line-height: 28px;
    margin: 0;
`;

const LaunchDateModalFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    flex-wrap: wrap;
    gap: 8px;
    padding: 0 16px 24px;
`;

export default UpdateApplicationFormInner;
